// Display formatting for an event's start/end instants, in the VIEWER's zone.
//
// The instant is the fact: the API stores and returns UTC (`timestamptz` ->
// `.toISOString()`), and every string below is that one instant rendered in
// whatever zone the reader is sitting in. A practitioner in Boston and one in
// Los Angeles see different clock times for the same meeting, which is the
// point - each sees the hour they need to show up.
//
// The three surfaces stay consistent because they all resolve the same way:
// the banner and modal read the browser's zone here, and the confirmation
// email is rendered server-side from the zone captured on the sign-up row
// (`event_signups.time_zone`), not from a canonical one.
//
// Always labelled: `formatEventTime` carries `timeZoneName: 'short'`, so every
// string says "CDT"/"EST" out loud rather than leaving the reader to guess
// whose clock it is - which matters more, not less, now that the answer varies.
//
// The `timeZone` parameter stays on every function so a caller can still
// override it (an admin previewing "what a Central doctor sees" needs no
// signature change).

/** Where the zone lands when the runtime cannot resolve one. */
export const EVENT_FALLBACK_TIME_ZONE = 'America/Chicago';

/**
 * The viewer's IANA zone. Resolved per call rather than cached at module load:
 * a long-lived tab can be moved across zones, and this is not a hot path.
 */
export function resolveViewerTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || EVENT_FALLBACK_TIME_ZONE;
    } catch {
        return EVENT_FALLBACK_TIME_ZONE;
    }
}

export function formatEventDate(iso: string, timeZone: string = resolveViewerTimeZone()): string {
    return new Date(iso).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone
    });
}

export function formatEventDateShort(
    iso: string,
    timeZone: string = resolveViewerTimeZone()
): string {
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone
    });
}

export function formatEventWeekdayShort(
    iso: string,
    timeZone: string = resolveViewerTimeZone()
): string {
    return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', timeZone });
}

export function formatEventTime(iso: string, timeZone: string = resolveViewerTimeZone()): string {
    return new Date(iso).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
        timeZone
    });
}

// The modal's single date/time line: "Thursday, July 23, 2026 at 1:00 PM CDT"
// or, when the event has a display end time (e.g. Bridge Club),
// "Thursday, May 19, 2026, 6:15 PM – 7:45 PM CDT".
export function formatEventWindow(
    startsAt: string,
    endsAt: string | null,
    timeZone: string = resolveViewerTimeZone()
): string {
    const date = formatEventDate(startsAt, timeZone);
    const startTime = formatEventTime(startsAt, timeZone);
    if (!endsAt) {
        return `${date} at ${startTime}`;
    }
    const endTime = formatEventTime(endsAt, timeZone);
    return `${date}, ${startTime} – ${endTime}`;
}
